import { EventType } from "@/analytics/analytics-events";
import { useThresholdSetContext } from "@/registration-tools/common/registration-report/threshold-set-context";
import { QualityStatus } from "@/registration-tools/utils/metrics";
import { determineCombinedMetricsQuality } from "@/registration-tools/utils/multi-registration-report";
import { useAppSelector } from "@/store/store-hooks";
import {
  blue,
  FaroIconButton,
  FaroSvgIcon,
  FaroTooltip,
  useIsDarkScheme,
} from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import {
  RegistrationEdgeType,
  RegistrationStatus,
} from "@faro-lotv/service-wires";
import { useMemo, useRef, useState, type JSX } from "react";
import { ReactComponent as RegistrationQualityIndicatorDefaultDark } from "../icons/registration-quality-indicator-default-dark.svg";
import { ReactComponent as RegistrationQualityIndicatorDefaultLight } from "../icons/registration-quality-indicator-default-light.svg";
import { ReactComponent as RegistrationQualityIndicatorGoodDark } from "../icons/registration-quality-indicator-good-dark.svg";
import { ReactComponent as RegistrationQualityIndicatorGoodLight } from "../icons/registration-quality-indicator-good-light.svg";
import { ReactComponent as RegistrationQualityIndicatorMediumDark } from "../icons/registration-quality-indicator-medium-dark.svg";
import { ReactComponent as RegistrationQualityIndicatorMediumLight } from "../icons/registration-quality-indicator-medium-light.svg";
import { ReactComponent as RegistrationQualityIndicatorPoorDark } from "../icons/registration-quality-indicator-poor-dark.svg";
import { ReactComponent as RegistrationQualityIndicatorPoorLight } from "../icons/registration-quality-indicator-poor-light.svg";
import { ReactComponent as RegistrationQualityIndicatorWarningDark } from "../icons/registration-quality-indicator-warning-dark.svg";
import { ReactComponent as RegistrationQualityIndicatorWarningLight } from "../icons/registration-quality-indicator-warning-light.svg";

import { selectHierarchicalRegistrationStatus } from "../store/registration-status-selectors";
import { selectEdgesByType } from "../store/revision/revision-edge-selectors";
import { selectRootEntity } from "../store/revision/revision-entity-selectors";
import { QualityPopover, QualityScope } from "./quality-popover";

export type ShowQualityReportButtonProps = {
  /** Callback, when the report should be opened */
  onShowReport(): void;
};

/**
 * @returns A button to show the status of the registration.
 */
export function RegistrationQualityIndicator({
  onShowReport,
}: ShowQualityReportButtonProps): JSX.Element | null {
  const { thresholdSet } = useThresholdSetContext();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const rootEntity = useAppSelector(selectRootEntity);
  const isDarkMode = useIsDarkScheme();
  const overallRegistrationStatus = useAppSelector(
    selectHierarchicalRegistrationStatus(rootEntity?.id),
  );
  const globalEdges = useAppSelector(
    selectEdgesByType(RegistrationEdgeType.global),
  );

  const overallQuality = useMemo(() => {
    if (rootEntity) {
      const { report } = rootEntity;

      if (
        overallRegistrationStatus !== RegistrationStatus.registered &&
        overallRegistrationStatus !== RegistrationStatus.lockedManually
      ) {
        return QualityStatus.WARNING;
      }

      // registration is successful and report exists
      if (report) {
        const { avgPointError, minOverlap, maxPointError } =
          report.cloudToCloud;
        const metrics = {
          averagePointError: avgPointError,
          minOverlap,
          maxPointError,
        };
        return determineCombinedMetricsQuality(metrics, thresholdSet);
      }

      // Registration is successful but no report exists
      if (overallRegistrationStatus === RegistrationStatus.registered) {
        return QualityStatus.UNKNOWN;
      }

      return QualityStatus.WARNING;
    }

    return QualityStatus.UNKNOWN;
  }, [rootEntity, overallRegistrationStatus, thresholdSet]);

  const svg = useMemo(() => {
    switch (overallQuality) {
      case QualityStatus.GOOD:
        return isDarkMode
          ? RegistrationQualityIndicatorGoodDark
          : RegistrationQualityIndicatorGoodLight;
      case QualityStatus.MEDIUM:
        return isDarkMode
          ? RegistrationQualityIndicatorMediumDark
          : RegistrationQualityIndicatorMediumLight;
      case QualityStatus.POOR:
        return isDarkMode
          ? RegistrationQualityIndicatorPoorDark
          : RegistrationQualityIndicatorPoorLight;
      case QualityStatus.WARNING:
        return isDarkMode
          ? RegistrationQualityIndicatorWarningDark
          : RegistrationQualityIndicatorWarningLight;
      default:
        return isDarkMode
          ? RegistrationQualityIndicatorDefaultDark
          : RegistrationQualityIndicatorDefaultLight;
    }
  }, [overallQuality, isDarkMode]);

  if (!rootEntity) {
    return null;
  }

  return (
    <>
      <FaroTooltip title="Registration Quality">
        <FaroIconButton
          size="l"
          ref={buttonRef}
          sx={{
            "&:hover": {
              // Add hover feedback
              outline: `${blue[500]} solid 1px`,
            },
            "& > :first-of-type": {
              // Avoid layout shifts due to margin
              margin: 0,
              // Use same size as the action buttons
              width: "2.25rem",
              height: "2.25rem",
            },
          }}
          onClick={() => {
            setIsPopoverOpen(true);
            Analytics.track(EventType.showSimpleRegistrationQualityReport);
          }}
        >
          <FaroSvgIcon
            source={svg}
            aria-label="Total Registration Quality Indicator"
          />
        </FaroIconButton>
      </FaroTooltip>
      {isPopoverOpen && (
        <QualityPopover
          aria-label="simple registration quality report"
          scope={QualityScope.CLUSTER}
          quality={overallQuality}
          anchorEl={buttonRef.current}
          isOpen={isPopoverOpen}
          onDetailsButtonClick={() => {
            setIsPopoverOpen(false);
            Analytics.track(EventType.showDetailedRegistrationQualityReport);
            onShowReport();
          }}
          closePopover={() => {
            setIsPopoverOpen(false);
          }}
          isReportInCaptureTree={!!rootEntity.report}
          areGlobalEdgesInRegistration={globalEdges.length > 0}
        />
      )}
    </>
  );
}
